/**
 * es-jogos — SDK dos jogos educativos do Ensino Social. Licença MIT.
 *
 * O CANO entre um jogo (games.ensinosocial.com.br) e o monolito
 * (?a=jogos.api, migração 300_jogos.sql). Um pacote separado de propósito:
 * os jogos portados são GPL e o monolito é proprietário; este SDK é MIT
 * pra poder viver dos dois lados sem contaminar ninguém.
 *
 * Autenticação: cookie de sessão ENSINO_SESSID (domain .ensinosocial.com.br,
 * SameSite=Lax) viaja sozinho no fetch com credentials. Sem login o
 * `reportar` recebe 401 e engole em silêncio: jogar deslogado é sempre
 * permitido, só não pontua.
 *
 * Estudo: EnsinoSocial/docs/pesquisa/games-estrategia-2026-08-23.md (F1, P-166).
 */

export interface UsuarioLogado {
  id: number;
  nome: string;
  avatar: string;
}

export interface QuemSou {
  logado: boolean;
  usuario?: UsuarioLogado;
}

export interface Conclusao {
  jogo: string;
  atividade?: string;
  nivel?: number;
  score?: number;
  score_max?: number;
  tentativas?: number;
  erros?: number;
  tempo_ms?: number;
  publico?: 'crianca' | 'adulto';
  params?: Record<string, unknown>;
}

export interface JogoDoCatalogo {
  slug: string;
  titulo: string;
  descricao: string;
  url: string;
  icone: string;
  gradiente: string;
  status: 'estavel' | 'beta' | 'em-breve';
  meta: Array<{ icone: string; texto: string }>;
  origem: string;
  licenca: string;
  idade_min: number | null;
  idade_max: number | null;
  total_atividades: number;
}

export interface ProgressoAtividade {
  atividade: string;
  nivel_max: number;
  melhor_score: number;
  conclusoes: number;
  em: string;
}

// ---------------------------------------------------------------------------
// Base da API (mesma detecção do RankingService do Logic Bugs)
// ---------------------------------------------------------------------------

export function apiBase(): string {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:8888';
    if (h.endsWith('sandbox.ensinosocial.com.br')) return 'https://sandbox.ensinosocial.com.br';
  }
  return 'https://ensinosocial.com.br';
}

function apiUrl(op: string): string {
  return `${apiBase()}/?a=jogos.api&op=${op}`;
}

export function loginUrl(): string {
  return `${apiBase()}/?a=auth.login`;
}

// ---------------------------------------------------------------------------
// quem-sou (com cache de sessão de página)
// ---------------------------------------------------------------------------

let quemSouCache: Promise<QuemSou> | null = null;

export function quemSou(forcar = false): Promise<QuemSou> {
  if (!quemSouCache || forcar) {
    quemSouCache = fetch(apiUrl('quem-sou'), { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<QuemSou>) : { logado: false }))
      .catch(() => ({ logado: false }));
  }
  return quemSouCache;
}

// ---------------------------------------------------------------------------
// catálogo e progresso
// ---------------------------------------------------------------------------

export async function catalogo(): Promise<JogoDoCatalogo[]> {
  const r = await fetch(apiUrl('catalogo'), { credentials: 'include' });
  if (!r.ok) throw new Error(`catalogo http ${r.status}`);
  const data = (await r.json()) as { sucesso: boolean; jogos?: JogoDoCatalogo[] };
  if (!data.sucesso || !Array.isArray(data.jogos)) throw new Error('catalogo invalido');
  return data.jogos;
}

export async function progresso(jogo: string): Promise<ProgressoAtividade[]> {
  const r = await fetch(`${apiUrl('progresso')}&jogo=${encodeURIComponent(jogo)}`, {
    credentials: 'include',
  });
  if (!r.ok) return [];
  const data = (await r.json()) as { sucesso: boolean; progresso?: ProgressoAtividade[] };
  return data.sucesso && Array.isArray(data.progresso) ? data.progresso : [];
}

// ---------------------------------------------------------------------------
// reportar (fire-and-forget; nunca quebra o jogo)
// ---------------------------------------------------------------------------

/** Normaliza uma conclusão: clamps e limpeza. Pura, testável. */
export function montarConclusao(c: Conclusao): Conclusao {
  const clamp = (v: unknown, min: number, max: number): number => {
    const n = Math.round(Number(v) || 0);
    return Math.max(min, Math.min(max, n));
  };
  const slug = (s: unknown, max: number): string =>
    String(s ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '')
      .slice(0, max);

  const out: Conclusao = {
    jogo: slug(c.jogo, 40),
    atividade: slug(c.atividade, 60),
    nivel: clamp(c.nivel, 0, 10000),
    score: clamp(c.score, 0, 100000),
    score_max: clamp(c.score_max, 0, 100000),
    tentativas: clamp(c.tentativas, 0, 10000),
    erros: clamp(c.erros, 0, 10000),
    tempo_ms: clamp(c.tempo_ms, 0, 7200000),
  };
  if (c.publico === 'crianca' || c.publico === 'adulto') out.publico = c.publico;
  if (c.params && typeof c.params === 'object') out.params = c.params;
  if ((out.erros ?? 0) > (out.tentativas ?? 0) && (out.tentativas ?? 0) > 0) {
    out.erros = out.tentativas;
  }
  return out;
}

export function reportar(c: Conclusao): void {
  const payload = montarConclusao(c);
  if (!payload.jogo) return;
  try {
    fetch(apiUrl('concluir'), {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      /* sem rede ou 401: jogar continua sendo grátis */
    });
  } catch {
    /* fetch indisponível: ignora */
  }
}

// ---------------------------------------------------------------------------
// Ponte de iframe (o jogo avisa a página-mãe do Ensino Social)
// ---------------------------------------------------------------------------

export type MensagemPonte =
  | { tipo: 'es:game:ready'; jogo: string }
  | { tipo: 'es:game:complete'; conclusao: Conclusao }
  | { tipo: 'es:game:exit'; jogo: string }
  | { tipo: 'es:game:resize'; altura: number };

function estaEmbedado(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    return false;
  }
}

function enviar(msg: MensagemPonte): void {
  if (!estaEmbedado()) return;
  try {
    // Conteúdo não-sensível (sinais de jogo); '*' permite embed no sandbox
    // e em páginas de teste sem quebrar.
    window.parent.postMessage(msg, '*');
  } catch {
    /* pai inacessível: ignora */
  }
}

export const ponte = {
  pronto(jogo: string): void {
    enviar({ tipo: 'es:game:ready', jogo });
  },
  concluido(c: Conclusao): void {
    enviar({ tipo: 'es:game:complete', conclusao: montarConclusao(c) });
  },
  sair(jogo: string): void {
    enviar({ tipo: 'es:game:exit', jogo });
  },
  /**
   * Observa o documento e avisa a página-mãe da altura do CONTEÚDO
   * (es:game:resize) — o pai ajusta o iframe e ninguém rola por dentro.
   *
   * Não dá pra depender só de ResizeObserver no <html>: com html/body em
   * height:100%, a caixa deles é o viewport — conteúdo crescendo dentro de
   * um iframe de altura fixa não muda a caixa e o observer nunca dispara
   * (foi exatamente o buraco da Atividade do dia). Por isso: Mutation +
   * Resize + resize do window, todos desaguando numa medida por frame.
   */
  autoResize(): () => void {
    if (!estaEmbedado()) return () => {};
    let ultima = 0;
    let agendado = false;
    const mede = (): void => {
      agendado = false;
      const alt = Math.max(
        document.documentElement ? document.documentElement.scrollHeight : 0,
        document.body ? document.body.scrollHeight : 0,
      );
      if (alt > 0 && alt !== ultima) {
        ultima = alt;
        enviar({ tipo: 'es:game:resize', altura: alt });
      }
    };
    // setTimeout, não rAF: rAF congela em aba/iframe em segundo plano e a
    // medida ficaria presa até a pessoa voltar — 50ms coalesce igual.
    const agenda = (): void => {
      if (agendado) return;
      agendado = true;
      setTimeout(mede, 50);
    };
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(agenda) : null;
    if (mo) {
      mo.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(agenda) : null;
    if (ro) {
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
    }
    window.addEventListener('resize', agenda);
    agenda();
    return () => {
      if (mo) mo.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener('resize', agenda);
    };
  },
};
